import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from 'src/components/button/button.component';
import { CheckboxToggleComponent } from 'src/components/checkbox/checkbox-toggle.component';
import { IconModule } from 'src/components/icon/icon.module';
import { InputDateComponent } from 'src/components/input/input-date.component';
import { InputImageComponent } from 'src/components/input/input-image.component';
import { InputNumberComponent } from 'src/components/input/input-number.component';
import { InputPhoneExtComponent } from 'src/components/input/input-phone-ext.component';
import { InputRadioComponent, InputRadioOption } from 'src/components/input/input-radio.component';
import {
  InputSelectComponent,
  InputSelectOption,
} from 'src/components/input/input-select.component';
import { InputTextComponent } from 'src/components/input/input-text.component';
import { InputTextareaComponent } from 'src/components/input/input-textarea.component';
import { Form, FormError } from 'src/definitions/form.definition';
import { CreateKaryawanForm, GenderType } from 'src/definitions/karyawan.definition';
import { DiffYearPipe } from 'src/pipes/date.pipe';
import { FormService } from 'src/services/form.service';
import { ToastService } from 'src/services/toast.service';
import { GlobalService } from 'src/services/global.service';

export type ProfileForm = Pick<
  CreateKaryawanForm,
  | 'image'
  | 'name'
  | 'nickname'
  | 'nationality'
  | 'place_of_birth'
  | 'birthday'
  | 'identity_number'
  | 'religion'
  | 'gender'
  | 'address_original'
  | 'blood_type'
  | 'npwp_number'
  | 'npwp_image'
  | 'ktp_image'
  | 'kontak_darurat'
  | 'nama_faskes'
  | 'kode_faskes'
  | 'weight'
  | 'height'
  | 'shirt_size'
  | 'pants_size'
  | 'shoe_size'
  | 'medical_history'
> & {
  has_medical_history: string;
  banks: (Pick<CreateKaryawanForm['banks'][0], 'bank_name' | 'account_number' | 'account_name'> & {
    is_primary: string;
  })[];
};

@Component({
  selector: 'step-1',
  templateUrl: './step-1.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconModule,
    ButtonComponent,
    InputDateComponent,
    InputTextComponent,
    InputRadioComponent,
    InputImageComponent,
    InputSelectComponent,
    InputNumberComponent,
    InputPhoneExtComponent,
    InputTextareaComponent,
    CheckboxToggleComponent,
    DiffYearPipe,
  ],
})
export class _Step1Component implements OnInit {
  @Input()
  form!: Form<ProfileForm>;

  @Input()
  formError!: FormError<ProfileForm>;

  @Output()
  next = new EventEmitter<void>();

  @Output()
  addBank = new EventEmitter<void>();

  @Output()
  removeBank = new EventEmitter<number>();

  @Output()
  addKontakDarurat = new EventEmitter<void>();

  @Output()
  removeKontakDarurat = new EventEmitter<number>();

  protected religionOptions: InputRadioOption = [
    { label: 'Islam', value: 'Islam' },
    { label: 'Katolik', value: 'Katolik' },
    { label: 'Protestan', value: 'Protestan' },
    { label: 'Hindu', value: 'Hindu' },
    { label: 'Budha', value: 'Budha' },
  ];

  protected genderOptions: InputRadioOption = [
    { label: 'Laki Laki', value: GenderType.LakiLaki },
    { label: 'Perempuan', value: GenderType.Perempuan },
  ];

  protected bloodTypeOptions: InputRadioOption = [
    { label: 'A', value: 'A' },
    { label: 'B', value: 'B' },
    { label: 'O', value: 'O' },
    { label: 'AB', value: 'AB' },
    { label: 'Belum Diketahui', value: 'N/A' },
  ];

  protected bankOptions: InputSelectOption<string>[] = [{ label: 'BCA', value: 'BCA' }];

  protected relationOption: InputRadioOption = [
    { label: 'Istri', value: 'Istri' },
    { label: 'Suami', value: 'Suami' },
    { label: 'Anak', value: 'Anak' },
    { label: 'Ibu', value: 'Ibu' },
    { label: 'Ayah', value: 'Ayah' },
    { label: 'Lainnya', value: 'Lainnya' },
  ];
  protected relationSelected: string[] = [];

  protected shirtSizeOptions: InputSelectOption<string>[] = [
    { label: 'XS', value: 'XS' },
    { label: 'S', value: 'S' },
    { label: 'M', value: 'M' },
    { label: 'L', value: 'L' },
    { label: 'XL', value: 'XL' },
    { label: 'XXL', value: '2XL' },
    { label: 'XXXL', value: '3XXL' },
  ];

  protected shoeSizeOptions: InputSelectOption<number, { label_in_cm: string }>[] = Object.values(
    ShoeSizeType,
  ).map((type) => ({
    label: `EU ${type}`,
    label_in_cm: `(${ShoeSizeTypeCmLabel[type]})`,
    value: type,
  }));

  protected hasMedicalHistoryOptions: InputRadioOption = [
    { label: 'Ada', value: 'true' },
    { label: 'Tidak Ada', value: 'false' },
  ];

  protected Validators = Validators;

  constructor(
    protected formService: FormService,
    protected toastService: ToastService,
    protected globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.__init();
  }

  protected _toggleBankPrimary(index: number) {
    const banks = this.form.controls.banks;
    const selectedBank = banks.controls[index];

    const isPrimary = selectedBank.controls.is_primary.value === 'true';
    if (isPrimary) {
      this.toastService.showToast({
        summary: 'Harus ada rekening utama',
        detail: 'Pilih rekening lain untuk dijadikan rekening utama',
        severity: 'error',
      });
      return;
    }

    banks.controls.forEach((bank, i) => {
      if (i !== index) bank.controls.is_primary.setValue('false');
    });
    selectedBank.controls.is_primary.setValue('true');
  }

  protected _removeBank(index: number) {
    const isPrimary = this.form.controls.banks.controls[index].controls.is_primary.value === 'true';
    if (isPrimary) this._toggleBankPrimary(index === 0 ? 1 : 0);
    this.removeBank.emit(index);
  }

  protected _changeRelation(relation: string, index: number) {
    this.relationSelected[index] = relation;
    this.form.controls.kontak_darurat.controls[index].controls.relation.setValue('');

    if (relation !== 'Lainnya') {
      this.formService.resetError(this.formError.kontak_darurat[index], 'relation');
      this.form.controls.kontak_darurat.controls[index].controls.relation.setValue(relation);
    }
  }

  protected _onHasMedicalHistoryChange() {
    const hasMedicalHistoryField = this.form.controls.has_medical_history;
    const medicalHistoryField = this.form.controls.medical_history;

    this.formService.resetError(this.formError, 'has_medical_history');

    if (hasMedicalHistoryField.value === 'true') {
      medicalHistoryField.setValidators([Validators.required]);
      medicalHistoryField.updateValueAndValidity();
      return;
    }

    medicalHistoryField.setValidators([]);
    medicalHistoryField.updateValueAndValidity();
  }

  protected _next() {
    if (this.form.invalid) {
      const invalid = this.formService.validateAndGetErrorMessage(this.form, this.formError);
      this.toastService.showToast({
        summary: invalid,
        detail: 'Cek ulang isian',
        severity: 'error',
      });
      return;
    }

    this.next.emit();
  }

  private __init() {
    this.form.value.kontak_darurat?.forEach((kontak) => {
      if (['Istri', 'Suami', 'Anak', 'Ibu', 'Ayah'].includes(kontak.relation || '')) {
        this.relationSelected.push(kontak.relation || '');
      } else {
        this.relationSelected.push('Lainnya');
      }
    });
  }
}

export const ShoeSizeType = {
  36: 36,
  37: 37,
  38: 38,
  39: 39,
  40: 40,
  41: 41,
  42: 42,
  43: 43,
  44: 44,
} as const;

export const ShoeSizeTypeCmLabel: Record<number, string> = {
  [ShoeSizeType[36]]: '22,1 cm',
  [ShoeSizeType[37]]: '22,9 cm',
  [ShoeSizeType[38]]: '23,3 cm',
  [ShoeSizeType[39]]: '24,2 cm',
  [ShoeSizeType[40]]: '24,6 cm',
  [ShoeSizeType[41]]: '25,5 cm',
  [ShoeSizeType[42]]: '25,9 cm',
  [ShoeSizeType[43]]: '26,7 cm',
  [ShoeSizeType[44]]: '27,1 cm',
};
